/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState } from "react";
import AbilityBlock from "components/ability/AbilityBlock";
import Section from "model/abilities/Section";
import NpcData from "model/monster/NpcData";


interface SectionElementProps {
  section: Section;
  index: number;
  optionsSelectedArrayByOptionNameMap: Map<string, string[]>;
  onSetOption: (keyName: string, newValues: string[]) => void;
  showSelect: boolean;
}

const SectionElement: React.FC<SectionElementProps> = ({
  section,
  index,
  optionsSelectedArrayByOptionNameMap,
  onSetOption,
  showSelect,
}) => {
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [spellbookOpen, setSpellbookOpen] = useState<boolean>(false);
  const handleSetOption = (
    keyName: string,
    values: string[],
    idx: number,
    value: string
  ) => {
    const newValues = [...values];
    newValues[idx] = value;
    onSetOption(keyName, newValues);
  };

  const renderSection = (sect: Section, ndx: number | string) => {
    const keyName = sect.name.trim();
    let selectedOptions = optionsSelectedArrayByOptionNameMap.get(keyName);

    if (showSelect && sect.options?.length === 0) {
      // We are showing only the select drop-down, and this section doesn't even have anything to select
      return null;
    }

    let allSelected = true;
    if (!selectedOptions) {
      selectedOptions = [""];
      allSelected = false;
    }
    for (const val of selectedOptions) {
      if (!val) {
        allSelected = false;
        break;
      }
    }

    const showSelectDropDown = showSelect && sect.options && sect.options.length > 0 && Array.isArray(selectedOptions);
    const showOptionHeader = !showSelect && (!allSelected || !sect.options?.length);
    const showOptions = !showSelect;
    const childrenToShow = sect.children?.filter(
      (child) => !allSelected || selectedOptions?.includes(child.name.trim())
    );

    const selectOption = (value: any, i: any, type: 'odd' | 'even' | 'all') => {
      if (i%2 == 0 && type === 'odd') {
        return;
      }
      if (i%2 == 1 && type === 'even') {
        return;
      }
      return (<React.Fragment key={i}>
              <select
                value={value || ""}
                onChange={(event) =>
                  handleSetOption(keyName, selectedOptions!, i, event.target.value)
                }
              >
                <option value="">-- Select --</option>
                {sect.options?.map((option) => (
                  <option key={option.trim()} value={option.trim()}>
                    {option.trim()}
                  </option>
                ))}
              </select>
              <br />
            </React.Fragment>);
    };

    return (
      <React.Fragment key={ndx}>
        {showSelectDropDown && (
          <table>
            <tbody>
              <tr>
                <td>
                  <b>{sect.name}:&nbsp;</b>
                </td>
                {(selectedOptions.length > 2) ? (<>
                    <td>
                      {selectedOptions.map((value, i) => selectOption(value, i, 'even'))}
                    </td>
                    <td>
                      {selectedOptions.map((value, i) => selectOption(value, i, 'odd'))}
                    </td>
                  </>
                ) : (
                  <td>
                    {selectedOptions.map((value, i) => selectOption(value, i, 'all'))}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        )}
        {showOptionHeader && (
          <AbilityBlock
            obj={sect}
            mon={null}
            npcData={null}
            sectionName={''}
            section={section}
            sectionOptions={['']}
            onSectionChange={((newName) => {})}
            blockType="class"
            isOpen={false}
            isFirst={false}
            isLast={false}
            setIsOpen={() => true}
            onDelete={() => { } }
            onMoveDown={(event: React.MouseEvent<HTMLButtonElement>) => { } }
            onMoveUp={(event: React.MouseEvent<HTMLButtonElement>) => { } }
            onSave={(newObj: Ability) => { } }
            onNpcUpdate={(newObj: NpcData) => { } }
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            spellbookOpen={spellbookOpen}
            setSpellbookOpen={setSpellbookOpen}
            onRefreshTour={() => {}}/>
        )}
        {showOptions &&
          childrenToShow?.map((child, i) =>
            renderSection(child, `${ndx}:${i}`)
          )}
      </React.Fragment>
    );
  };

  if (section.fnDisplaySection && !showSelect) {
    const newSection = section.fnDisplaySection(optionsSelectedArrayByOptionNameMap);
    return newSection ? renderSection(newSection, index) : null;
  }
  
  return renderSection(section, index);
};

export default SectionElement;
